import React, { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Text } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import ErrorPage from "../../../../pages/Error";

import { buildBucketsForAvgIncreaseWidget, enhanceManagerGroupData } from "./utils";

import { CycleBudgetSummary } from "../SharedComponents/CycleBudgetSummary";
import { useCycleBudgetOverview } from "../SharedComponents/useCycleBudgetOverview";
import { AverageCompensationIncreaseWidget } from "./Widgets/AvgIncreaseWidget";
import { CycleBudgetsWidget } from "./Widgets/CycleBudgetsWidget";
import { TotalBudgetBreakdown } from "./Widgets/TotalBudgetBreakdownWidget";

export const CycleOverview: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const {
		cycleBudget,
		approvedSpend,
		percentIncrease,
		approvedBudgetCount,
		submittedBudgetCount,
		notSubmittedBudgetCount,
		approvedBudgetsPercent,
		notApprovedBudgetsPercent,
		isLoading,
		isForbidden,
		isNotFound,
		submittedSpend,
		notSubmittedEmployeeCount,
		employeesApproved,
		employeesSubmitted,
		approvedIncreasePercentsArray,
		averageApprovedPercentIncrease,
		medianApprovedIncreasePerEmployee,
		managerGroups,
		notApprovedSpend,
	} = useCycleBudgetOverview(cycleId);

	const enhancedManagerGroups = useMemo(() => enhanceManagerGroupData(managerGroups ?? []), [managerGroups]);

	const buckets = useMemo(() => {
		const sorted = [...(approvedIncreasePercentsArray ?? [])].sort((a, b) => a - b);
		return buildBucketsForAvgIncreaseWidget(sorted);
	}, [approvedIncreasePercentsArray]);

	const avgApprovedPerEmployee = useMemo(
		() => (employeesApproved > 0 ? approvedSpend / employeesApproved : 0),
		[employeesApproved, approvedSpend]
	);

	if (isLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (isNotFound) {
		return <ErrorPage code={404} />;
	}

	if (isForbidden) {
		return <ErrorPage code={403}></ErrorPage>;
	}

	return (
		<div>
			<CycleBudgetSummary
				cycleBudget={cycleBudget}
				approvedSpend={approvedSpend}
				percentIncrease={percentIncrease}
				approvedBudgetCount={approvedBudgetCount}
				submittedBudgetCount={submittedBudgetCount}
				notSubmittedBudgetCount={notSubmittedBudgetCount}
				approvedBudgetsPercent={approvedBudgetsPercent}
				notApprovedBudgetsPercent={notApprovedBudgetsPercent}
				notApprovedSpend={notApprovedSpend}
			/>
			<div className="mt-6 flex w-full gap-4">
				<div className="box-border flex h-[296px] w-1/2 min-w-0 flex-col items-start gap-2 rounded-lg border border-solid border-neutral-2 bg-neutral-0 p-4">
					<Text size="paragraph-3" weight="semi-bold">
						Average Compensation Increases
					</Text>
					<AverageCompensationIncreaseWidget
						avgApprovedPerEmployee={avgApprovedPerEmployee}
						averageApprovedPercentIncrease={averageApprovedPercentIncrease}
						medianApprovedIncreasePerEmployee={medianApprovedIncreasePerEmployee}
						buckets={buckets}
					></AverageCompensationIncreaseWidget>
				</div>
				<TotalBudgetBreakdown
					approvedSpend={approvedSpend}
					submittedSpend={submittedSpend}
					approvedBudgetCount={approvedBudgetCount}
					submittedBudgetCount={submittedBudgetCount}
					notSubmittedEmployeeCount={notSubmittedEmployeeCount}
					employeesApproved={employeesApproved}
					employeesSubmitted={employeesSubmitted}
					notApprovedSpend={notApprovedSpend}
				/>
			</div>
			<CycleBudgetsWidget enhancedManagerGroups={enhancedManagerGroups}></CycleBudgetsWidget>
		</div>
	);
};
export default CycleOverview;
